import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../components/shared/ToastNotification';
import api from '../../services/api';

interface DeliveryLog {
  id: string;
  status: string;
  date: string;
  quantity?: number;
  notes?: string;
  customer: {
    id: string;
    name: string;
    phone?: string;
    address?: string;
    delivery_instructions?: string;
  };
  product: { id: string; name: string };
  subscription: { id: string; delivery_time: string; unit_price?: number };
}

interface TodayData {
  date: string;
  summary: { total: number; pending: number; delivered: number; skipped: number; returned: number };
  morning: DeliveryLog[];
  evening: DeliveryLog[];
  history: DeliveryLog[];
  future: DeliveryLog[];
}

const STATUS_CONFIG = {
  pending:    { label: 'Pending',   color: 'bg-slate-50 text-slate-500 border-slate-200',   dot: 'bg-slate-300' },
  delivered:  { label: 'Delivered', color: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500' },
  skipped:    { label: 'Skipped',   color: 'bg-amber-50 text-amber-700 border-amber-100',    dot: 'bg-amber-500' },
  returned:   { label: 'Returned',  color: 'bg-rose-50 text-rose-600 border-rose-100',        dot: 'bg-rose-500' },
  free_replacement: { label: 'Replacement', color: 'bg-sky-50 text-sky-600 border-sky-100', dot: 'bg-sky-500' },
};

function DeliveryCard({
  log,
  onStatusChange,
}: {
  log: DeliveryLog;
  onStatusChange: (id: string, status: string, notes?: string) => void;
}) {
  const [localStatus, setLocalStatus] = useState(log.status);
  const [notes, setNotes] = useState(log.notes || '');
  const [expanded, setExpanded] = useState(false);
  const cfg = STATUS_CONFIG[localStatus as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending;

  const handleStatus = (s: string) => {
    setLocalStatus(s);
    onStatusChange(log.id, s, notes);
  };

  return (
    <div className={`bg-white rounded-3xl border shadow-sm transition-all duration-300 overflow-hidden ${
      localStatus === 'delivered' ? 'border-emerald-200 shadow-emerald-50/50' : 
      localStatus === 'skipped' ? 'border-amber-200 shadow-amber-50/50' : 
      'border-slate-100 shadow-slate-100/50'
    }`}>
      {/* Date badge if not today */}
      {log.date && (
        <div className="px-5 py-1.5 bg-slate-50 border-b border-slate-100 flex justify-between items-center text-[10px] font-black text-slate-400 uppercase tracking-widest">
          <span>Schedule Entry</span>
          <span>{new Date(log.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
        </div>
      )}

      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 animate-pulse ${cfg.dot}`} />
              <h4 className="font-black text-slate-900 text-base leading-tight truncate tracking-tight">{log.customer.name}</h4>
            </div>
            {log.customer.phone && (
              <div className="flex items-center gap-2 mt-1 ml-4.5">
                <svg className="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                <p className="text-xs text-slate-400 font-bold">{log.customer.phone}</p>
              </div>
            )}
            {log.customer.delivery_instructions && (
              <div className="mt-2 ml-4.5 p-2 bg-indigo-50/50 rounded-xl border border-indigo-100">
                <p className="text-[10px] text-indigo-600 font-black leading-tight italic tracking-tight">{log.customer.delivery_instructions}</p>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 text-right bg-slate-50 px-3 py-2 rounded-2xl border border-slate-100">
            <div className="font-black text-slate-900 text-lg leading-none">{log.quantity ?? '—'} <span className="text-[10px] text-slate-400 uppercase tracking-widest">units</span></div>
            <div className="text-[10px] text-slate-500 font-bold mt-1 max-w-[80px] truncate">{log.product.name}</div>
            {log.subscription.unit_price && (
              <div className="text-xs text-indigo-600 font-black mt-1">₹{(Number(log.quantity) * log.subscription.unit_price).toFixed(2)}</div>
            )}
          </div>
        </div>

        {/* Action Controls */}
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(['delivered', 'skipped', 'returned', 'free_replacement'] as const).map(s => {
              const sc = STATUS_CONFIG[s];
              return (
                <button
                  key={s}
                  onClick={() => handleStatus(s)}
                  className={`px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border-2 flex-1 min-w-[100px] text-center ${
                    localStatus === s
                      ? `${sc.color} border-current scale-[1.02] shadow-xl shadow-slate-100`
                      : 'bg-white border-slate-50 text-slate-400 hover:border-indigo-100 hover:text-indigo-500 active:scale-95'
                  }`}
                >
                  {sc.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex-1 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2 group"
            >
              <div className="w-4 h-px bg-slate-100 transition-all group-hover:w-8 group-hover:bg-indigo-100" />
              {expanded ? 'Hide Details' : 'Add Status Note'}
              <div className="w-4 h-px bg-slate-100 transition-all group-hover:w-8 group-hover:bg-indigo-100" />
            </button>
          </div>

          {expanded && (
            <div className="animate-in slide-in-from-top-2 fade-in duration-300">
              <input
                type="text"
                value={notes}
                placeholder="Log internal delivery details..."
                onChange={e => {
                  setNotes(e.target.value);
                  onStatusChange(log.id, localStatus, e.target.value);
                }}
                className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-xs font-bold focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 text-slate-900 transition-all"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeliveryTodayView() {
  const [data, setData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<Record<string, { status: string; notes?: string }>>({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState<'morning' | 'evening' | 'history' | 'future'>('morning');
  const { success, error } = useToast();

  const fetchToday = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/deliveries/today`, { date: selectedDate });
      if (res.success) {
        setData(res.data);
        setPendingUpdates({});
      } else {
        error(res.message || 'Failed to load deliveries');
      }
    } catch { 
      error('Failed to load deliveries'); 
    } finally { 
      setLoading(false); 
    }
  }, [selectedDate, error]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  const handleStatusChange = (id: string, status: string, notes?: string) => {
    setPendingUpdates(prev => ({ ...prev, [id]: { status, notes } }));
  };

  const handleBulkSave = async () => {
    const updates = Object.entries(pendingUpdates).map(([id, u]) => ({ id, ...u }));
    if (!updates.length) return;

    setSaving(true);
    try {
      const res = await api.post('/deliveries/bulk_update', { deliveries: updates });
      if (res.success) {
        const d = res.data;
        success(`✓ ${d.updated_count} deliveries updated${d.failed_count ? `, ${d.failed_count} failed` : ''}`);
        fetchToday();
      } else {
        error(res.message || 'Bulk update failed');
      }
    } catch { 
      error('An error occurred'); 
    } finally { 
      setSaving(false); 
    }
  };

  const pendingCount = Object.keys(pendingUpdates).length;
  const logs = data ? (
    activeTab === 'morning' ? data.morning : 
    activeTab === 'evening' ? data.evening :
    activeTab === 'history' ? data.history : 
    data.future
  ) : [];

  return (
    <div className="pt-6 space-y-8 animate-in fade-in duration-700">
      {/* Premium Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-2 border-b border-slate-100">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Delivery <span className="text-indigo-600">Central</span></h1>
          <p className="text-slate-500 font-medium text-sm flex items-center gap-2">
             <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
             {data?.summary.total || 0} commercial units tracking for global operations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative group">
            <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-600 group-focus-within:text-indigo-400">
               <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
            <input
              id="delivery-date"
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              className="pl-10 pr-4 py-3 bg-white border-2 border-slate-100 rounded-2xl text-sm focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 text-slate-900 font-black shadow-sm transition-all"
            />
          </div>
        </div>
      </div>

      {/* Modern Summary Stats */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Logs', value: data.summary.total, color: 'indigo', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { label: 'Pending', value: data.summary.pending, color: 'amber', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Delivered', value: data.summary.delivered, color: 'emerald', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
            { label: 'Alerts', value: data.summary.skipped + data.summary.returned, color: 'rose', icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' },
          ].map(s => (
            <div key={s.label} className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm shadow-slate-200/50 hover:shadow-xl hover:shadow-indigo-100/30 transition-all duration-500 group">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 duration-500 ${
                    s.color === 'indigo' ? 'bg-indigo-50 text-indigo-600' :
                    s.color === 'amber' ? 'bg-amber-50 text-amber-600' :
                    s.color === 'emerald' ? 'bg-emerald-50 text-emerald-600' :
                    'bg-rose-50 text-rose-600'
                  }`}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d={s.icon} /></svg>
                  </div>
                  <span className="text-2xl font-black text-slate-900 tracking-tighter">{s.value}</span>
                </div>
                <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 group-hover:text-indigo-600 transition-colors">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Premium Navigation Tabs */}
      <div className="flex gap-2 bg-white/50 backdrop-blur-md p-2 rounded-[2.5rem] border border-slate-200 shadow-sm max-w-2xl mx-auto">
        {(['history', 'morning', 'evening', 'future'] as const).map(tab => {
          const tabConfig = {
            history: { label: 'History', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
            morning: { label: 'Morning', icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z' },
            evening: { label: 'Evening', icon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z' },
            future: { label: 'Future', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' }
          }[tab];
          
          const tabCount = data ? data[tab].length : 0;
          
          return (
            <button
              key={tab}
              id={`tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 px-4 rounded-[2rem] font-black text-[10px] uppercase tracking-[0.2em] transition-all relative overflow-hidden flex flex-col items-center gap-1.5 ${
                activeTab === tab ? 'bg-indigo-600 shadow-xl shadow-indigo-200 text-white' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d={tabConfig.icon} /></svg>
                <span className="hidden md:inline">{tabConfig.label}</span>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-black min-w-[24px] ${
                activeTab === tab ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-500'
              }`}>
                {tabCount}
              </span>
            </button>
          );
        })}
      </div>

      {/* Delivery cards */}
      {loading ? (
        <div className="py-20 text-center text-slate-400">Loading deliveries...</div>
      ) : logs.length === 0 ? (
        <div className="py-16 bg-white rounded-3xl border border-dashed border-slate-300 text-center">
          <div className="text-4xl mb-2">{activeTab === 'morning' ? '🌅' : '🌆'}</div>
          <p className="text-slate-500 font-medium">No {activeTab} deliveries for {selectedDate}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {logs.map(log => (
            <DeliveryCard key={log.id} log={log} onStatusChange={handleStatusChange} />
          ))}
        </div>
      )}

      {/* Sticky save bar */}
      {pendingCount > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <button
            id="bulk-save-btn"
            onClick={handleBulkSave}
            disabled={saving}
            className="flex items-center gap-3 px-6 py-3.5 bg-indigo-600 text-white rounded-2xl font-bold shadow-2xl shadow-indigo-300 hover:bg-indigo-700 transition-all disabled:opacity-70"
          >
            {saving ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            )}
            Save {pendingCount} Update{pendingCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
}
